<script lang="ts">
  import {step} from '@/store';
  import {STEP} from '@/types/common';
  import {httpClient} from '@/utils/http-client';
  import {topnav} from '@/utils/top-nav-next-custom-events';
  import {onMount} from 'svelte';
  import Swal from 'sweetalert2';
  import AddTrialDialog from './components/AddTrialDialog.svelte';
  type P = Promise<any> | null;
  let user_id = '';
  let userList: P = null;
  let userData: P = null;
  let current = '';
  let isTrialDialogOpen: boolean = false;
  let activeMembership: string = '';

  const client = httpClient;
  function clearSearch() {
    userData = null;
    userList = null;
  }
  function searchUser() {
    userData = null;
    userList = httpClient.get(`/users/search?term=${encodeURIComponent(user_id)}`);
  }
  function viewUser(id: string) {
    getUsage(id);
  }
  function getUsage(id: string) {
    activeMembership = '';
    userData = client.get('/user-stats/user/' + id);
    userData.then(response => {
      activeMembership = response.data.membership.title || '';
    });
    current = id;
  }

  function downloadVideo(id: string) {
    let url = brushEncoder.url('encode/files/' + id + '?filename=SermonShots');
    return url;
  }

  function reload() {
    getUsage(current);
  }

  async function requestCreditUpdate(d: number) {
    let newvalue = parseFloat(prompt('Update credit', d.toString()) || '');
    if (isNaN(newvalue)) return;
    await httpClient.patch('/usage/credit', {
      owner: current,
      amount: newvalue
    });
    reload();
  }

  async function removeFromBeta() {
    await client.delete('/beta/' + current);
    reload();
  }

  async function addToBeta() {
    await client.post('/beta/' + current);
    reload();
  }

  async function removeBetaUsers() {
    Swal.fire({
      title: 'Remove ALL Beta Users',
      text: 'Are you sure you want to remove ALL Beta Users?',
      icon: 'question',
      showDenyButton: true,
      showCancelButton: false,
      confirmButtonText: 'Yes',
      denyButtonText: 'No',
      allowOutsideClick: () => !Swal.isLoading()
    }).then(async result => {
      if (result.isConfirmed) {
        await client
          .patch('/beta/clear')
          .then(() => {
            Swal.fire({
              position: 'bottom-end',
              icon: 'success',
              title: 'Beta Users Cleared',
              showConfirmButton: false,
              timer: 1500,
              toast: true
            });
          })
          .catch(error => {
            Swal.fire({
              position: 'bottom-end',
              icon: 'error',
              title: 'Request Failed',
              showConfirmButton: false,
              timer: 1500,
              toast: true
            });
          });
        return true;
      }
      return false;
    });
  }

  async function validateMembership(userId: string) {
    await client
      .get('/users/user/membership/' + userId)
      .then(response => {
        activeMembership = response.data.membership.title;
        Swal.fire({
          icon: 'success',
          title: `Membership Validated`,
          text: `${response.data.membership.title}`,
          showConfirmButton: false,
          timer: 1500
        });
      })
      .catch(error => {
        Swal.fire({
          icon: 'error',
          title: `Membership Validation Failed`,
          text: `${error.message}`,
          showConfirmButton: false,
          timer: 1500
        });
      });
  }

  async function validateMemberships() {
    Swal.fire({
      title: 'Fix Membership Record',
      text: 'Are you sure you want to re-import membership details',
      icon: 'question',
      showDenyButton: true,
      showCancelButton: false,
      confirmButtonText: 'Yes',
      denyButtonText: 'No',
      allowOutsideClick: () => !Swal.isLoading()
    }).then(async result => {
      if (result.isConfirmed) {
        await client
          .patch('/membership/validate')
          .then(() => {
            Swal.fire({
              position: 'bottom-end',
              icon: 'success',
              title: 'Membership Records Imported',
              showConfirmButton: false,
              timer: 1500,
              toast: true
            });
          })
          .catch(error => {
            Swal.fire({
              position: 'bottom-end',
              icon: 'error',
              title: 'Request Failed',
              showConfirmButton: false,
              timer: 1500,
              toast: true
            });
          });
        return true;
      }
      return false;
    });
  }

  async function openTrialDialog() {
    isTrialDialogOpen = true;
  }

  onMount(() => {
    userList = httpClient.get('/users/');
    topnav.dispatchEvent({nextDisabled: true});

    topnav.disabledNext(() => {});
    topnav.next(() => {});
    topnav.previous(() => {
      step.update(STEP.CHOOSE_VIDEO);
    });
  });
</script>

<div class="flex h-full w-full">
  <div class="flex h-full w-1/4 flex-col bg-white">
    <div class="flex justify-between space-x-4 bg-gray-300 p-4">
      <div class="w-full">
        <div class="relative flex h-12 w-full items-center overflow-hidden rounded-lg bg-white focus-within:shadow-lg">
          <div class="grid h-full w-12 place-items-center text-gray-700">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          <input
            bind:value={user_id}
            on:input={searchUser}
            class="peer h-full w-full pr-2 text-sm text-gray-700 outline-none"
            type="text"
            id="search"
            placeholder="User ID"
          />
        </div>
      </div>
      <div class="flex items-center justify-center">
        <div class=" dropdown relative inline-block text-left">
          <span class="rounded-md shadow-sm">
            <button
              class="focus:shadow-outline-blue inline-flex w-full justify-center rounded-lg bg-white p-3 text-sm font-medium leading-5 text-gray-700 transition duration-150 ease-in-out hover:text-gray-500 focus:border-blue-300 focus:outline-none active:bg-gray-50 active:text-gray-800"
              type="button"
              aria-haspopup="true"
              aria-expanded="true"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                class="bi bi-three-dots-vertical text-gray-500 hover:text-red-400"
                viewBox="0 0 16 16"
              >
                <path
                  d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"
                />
              </svg>
            </button></span
          >
          <div
            class="dropdown-menu invisible origin-top-right -translate-y-2 scale-95 transform opacity-0 transition-all duration-300"
          >
            <div
              class="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md border border-gray-200 bg-white shadow-lg outline-none"
              aria-labelledby="headlessui-menu-button-1"
              id="headlessui-menu-items-117"
              role="menu"
            >
              <div class="py-1">
                <a
                  href="#"
                  tabindex="0"
                  class="flex w-full justify-between px-4 py-2 text-left text-sm leading-5 text-gray-700"
                  role="menuitem"
                  on:click={removeBetaUsers}>Remove All Beta Users</a
                >
                <a
                  href="#"
                  tabindex="1"
                  class="flex w-full justify-between px-4 py-2 text-left text-sm leading-5 text-gray-700"
                  role="menuitem"
                  on:click={validateMemberships}>Validate Membership Records</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex-grow overflow-y-scroll">
      {#if userList}
        {#await userList}
          Loading users...
        {:then response}
          {#each response.data.data as item}
            <div class="w-full border-b border-b-gray-100 bg-gray-50 p-2">
              {item.owner} | {item.email}
              <button class="bg-gray-200 hover:bg-gray-300" on:click={() => viewUser(item.owner)}>View</button>
            </div>
          {/each}
        {/await}
      {/if}
    </div>
  </div>

  {#if userData}
    <div class="grid h-full flex-grow grid-rows-2 bg-gray-100">
      {#await userData}
        Loading...
      {:then response}
        <div class="overflow-y-scroll">
          <div class="flex w-full space-x-2 bg-gray-200 p-2">
            <div class="smallBlck">
              Credits: {response.data.credit.amount}
              <button on:click={() => requestCreditUpdate(response.data.credit.amount)}>Change Credit </button>
            </div>
            <div class="smallBlck">
              Total Usage: {response.data.usage}
            </div>
            <div class="smallBlck">
              Beta user: {response.data.beta ? 'Yes' : 'No'}
              {#if response.data.beta}
                <button on:click={removeFromBeta}>Remove From Beta</button>
              {:else}
                <button on:click={addToBeta}>Add to beta</button>
              {/if}
            </div>
            <div class="smallBlck">
              Full Access: {response.data.hasFullAccess ? 'Yes' : 'No'}
              <button on:click={openTrialDialog}>Update Full Access</button>
            </div>
            <div class="smallBlck">
              Membership: {activeMembership ? activeMembership : response.data.membership.title}
              <button on:click={() => validateMembership(response.data.userId)}>Validate Membership</button>
            </div>
          </div>
          <div class="mb-4 flex bg-white p-4">
            <h2 class="text-2xl font-bold">Template Selection</h2>
          </div>
          <table class="w-full table-auto text-center">
            <thead>
              <tr>
                <th>ID</th>
                <th>DIMENSIONS</th>
                <th>PREVIEW</th>
              </tr>
            </thead>
            <tbody>
              {#each response.data.templates as item}
                <tr>
                  <td>{item.template.id}</td>
                  <td>{item.template.width}x{item.template.height}</td>
                  <td><img class="mx-auto" src={item.template.preview.publicUrl} width="75" /></td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
        <div class="overflow-y-scroll">
          <div class="mb-4 flex bg-white p-4">
            <h2 class="text-2xl font-bold">Recent Downloads</h2>
          </div>
          <table class="w-full table-auto text-center">
            <thead>
              <tr>
                <th>Date</th>
                <th>Template</th>
                <th>Preview Link</th>
              </tr>
            </thead>
            <tbody>
              {#each response.data.downloads as item}
                <tr>
                  <td>{item.createdAt}</td>
                  <td>
                    {#if item.template}<img class="mx-auto" src={item.template.preview.publicUrl} width="75" />
                    {:else}
                      No template avaialbe{/if}</td
                  >
                  <td
                    ><a class="text-blue-500 hover:underline" href={item.metadata.videoData.url} target="_blank"
                      >{item.metadata.videoData.url}</a
                    ></td
                  >
                  <td
                    ><a
                      class="bg-blue-300 p-2 hover:bg-blue-400"
                      target="_blank"
                      href={downloadVideo(item.metadata.requestId)}>Final Download</a
                    ></td
                  >
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/await}
    </div>
  {/if}
</div>

<AddTrialDialog bind:isTrialDialogOpen user={current} on:reload-user-data={reload}></AddTrialDialog>

<style lang="postcss">
  td {
    @apply p-2;
  }

  .smallBlck {
    @apply bg-white p-2;
  }

  button {
    @apply text-blue-600 hover:underline;
  }

  .dropdown:focus-within .dropdown-menu {
    opacity: 1;
    transform: translate(0) scale(1);
    visibility: visible;
  }
</style>
